import { AxiosInstance } from 'axios';
import {
  AddLinesRequest,
  AddParticipantsRequest,
  AwardTenderRequest,
  BulkDeleteLinesResponse,
  BulkItemLookupResponse,
  CreateTenderRequest,
  ItemCandidate,
  ListTendersFilters,
  PortalView,
  SubmitOfferRequest,
  SupplierCandidate,
  SupplierContact,
  Tender,
  TenderDetail,
  TenderLine,
  TenderListResponse,
  TenderNotification,
  TenderOffersResponse,
  TenderParticipant,
  TenderStatusCounts,
  UpdateLineRequest,
  UpdateTenderMetaRequest,
} from './sourcing.types';

const BASE = '/v1/sourcing';
const PORTAL = '/v1/sourcing-portal';

export const createSourcingApi = (api: AxiosInstance) => ({
  list: (filters?: ListTendersFilters) =>
    api
      .get<TenderListResponse>(`${BASE}/tenders`, { params: filters })
      .then((r) => r.data),

  detail: (id: string) =>
    api.get<TenderDetail>(`${BASE}/tenders/${id}`).then((r) => r.data),

  offers: (id: string) =>
    api
      .get<TenderOffersResponse>(`${BASE}/tenders/${id}/offers`)
      .then((r) => r.data),

  notifications: (id: string) =>
    api
      .get<TenderNotification[]>(`${BASE}/tenders/${id}/notifications`)
      .then((r) => r.data),

  create: (body: CreateTenderRequest) =>
    api.post<Tender>(`${BASE}/tenders`, body).then((r) => r.data),

  updateMeta: (id: string, body: UpdateTenderMetaRequest) =>
    api.patch<Tender>(`${BASE}/tenders/${id}`, body).then((r) => r.data),

  publish: (id: string) =>
    api.post<Tender>(`${BASE}/tenders/${id}/publish`).then((r) => r.data),

  close: (id: string) =>
    api.post<Tender>(`${BASE}/tenders/${id}/close`).then((r) => r.data),

  cancel: (id: string) =>
    api.post<Tender>(`${BASE}/tenders/${id}/cancel`).then((r) => r.data),

  award: (id: string, body: AwardTenderRequest) =>
    api.post<Tender>(`${BASE}/tenders/${id}/award`, body).then((r) => r.data),

  addLines: (id: string, body: AddLinesRequest) =>
    api
      .post<TenderLine[]>(`${BASE}/tenders/${id}/lines`, body)
      .then((r) => r.data),

  updateLine: (id: string, lineId: string, body: UpdateLineRequest) =>
    api
      .patch<TenderLine>(`${BASE}/tenders/${id}/lines/${lineId}`, body)
      .then((r) => r.data),

  removeLine: (id: string, lineId: string) =>
    api.delete(`${BASE}/tenders/${id}/lines/${lineId}`).then((r) => r.data),

  bulkDeleteLines: (id: string, lineIds: string[]) =>
    api
      .post<BulkDeleteLinesResponse>(`${BASE}/tenders/${id}/lines/bulk-delete`, {
        lineIds,
      })
      .then((r) => r.data),

  addParticipants: (id: string, body: AddParticipantsRequest) =>
    api
      .post<TenderParticipant[]>(`${BASE}/tenders/${id}/participants`, body)
      .then((r) => r.data),

  removeParticipant: (id: string, participantId: string) =>
    api
      .delete(`${BASE}/tenders/${id}/participants/${participantId}`)
      .then((r) => r.data),

  linkContactList: (id: string, listId: number) =>
    api
      .post<TenderParticipant[]>(
        `${BASE}/tenders/${id}/contact-lists/${listId}`,
      )
      .then((r) => r.data),

  unlinkContactList: (id: string, listId: number) =>
    api
      .delete<TenderParticipant[]>(
        `${BASE}/tenders/${id}/contact-lists/${listId}`,
      )
      .then((r) => r.data),

  itemCandidates: (q: string) =>
    api
      .get<ItemCandidate[]>(`${BASE}/item-candidates`, { params: { q } })
      .then((r) => r.data),

  itemBulkLookup: (tokens: string[]) =>
    api
      .post<BulkItemLookupResponse>(`${BASE}/item-candidates/bulk-lookup`, {
        tokens,
      })
      .then((r) => r.data),

  statusCounts: () =>
    api.get<TenderStatusCounts>(`${BASE}/status-counts`).then((r) => r.data),

  duplicate: (id: string) =>
    api.post<Tender>(`${BASE}/tenders/${id}/duplicate`).then((r) => r.data),

  resendInvitation: (id: string, participantId: string) =>
    api
      .post<{ enqueued: true }>(
        `${BASE}/tenders/${id}/participants/${participantId}/resend`,
      )
      .then((r) => r.data),

  supplierCandidates: (q: string) =>
    api
      .get<SupplierCandidate[]>(`${BASE}/supplier-candidates`, {
        params: { q },
      })
      .then((r) => r.data),

  supplierContacts: (supplierId: number) =>
    api
      .get<SupplierContact[]>(
        `${BASE}/supplier-candidates/${supplierId}/contacts`,
      )
      .then((r) => r.data),

  // --- Supplier portal (token-gated, no auth) ----------------------------

  portalView: (token: string) =>
    api.get<PortalView>(`${PORTAL}/tenders/${token}`).then((r) => r.data),

  submitOffer: (token: string, body: SubmitOfferRequest) =>
    api
      .put<{ accepted: number }>(`${PORTAL}/tenders/${token}/offers`, body)
      .then((r) => r.data),
});
